/**
 * Chat Input Component
 * Following PRD Phase 3 specifications (lines 360-366)
 * Updated with file upload capability
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Paperclip, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CHARACTER_LIMIT } from '@/types/chat';
import { cn } from '@/lib/utils';
import { FileAttachment } from './FileAttachment';
import { useChatStore } from '@/stores/chatStore';

interface ChatInputProps {
  onSendMessage: (message: string, fileIds?: string[]) => void;
  onInterrupt?: () => void;
  onSendGuidance?: (content: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  canInterrupt?: boolean;
  executionState?: 'idle' | 'running' | 'interrupted' | 'completed';
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onInterrupt,
  onSendGuidance,
  isLoading = false,
  disabled = false,
  placeholder = "Ask AccountsGenie anything...",
  canInterrupt = false,
  executionState = 'idle'
}) => {
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});

  // FEATURE: Reload prompts and skills button state
  // PURPOSE: Allow manual reload of prompts/skills from database via frontend button
  // TRACEABILITY: Calls POST /api/prompts/reload endpoint in backend
  const [isReloading, setIsReloading] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get session ID from Zustand store
  const { currentSession, sessionId: storeSessionId } = useChatStore();

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // Global ESC key listener for interrupt (works even when textarea not focused)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canInterrupt && onInterrupt) {
        e.preventDefault();
        console.log('🛑 Global ESC pressed - interrupting execution');
        onInterrupt();
      }
    };

    if (canInterrupt) {
      document.addEventListener('keydown', handleGlobalKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [canInterrupt, onInterrupt]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      // Validate file size (50MB limit)
      const MAX_SIZE = 50 * 1024 * 1024;
      const validFiles = files.filter(file => {
        if (file.size > MAX_SIZE) {
          setUploadErrors(prev => ({
            ...prev,
            [file.name]: 'File exceeds 50MB limit'
          }));
          return false;
        }
        return true;
      });

      setSelectedFiles(prev => [...prev, ...validFiles]);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    // Remove corresponding upload data
    const fileName = selectedFiles[index].name;
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fileName];
      return newErrors;
    });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const trimmedMessage = message.trim();

    // During execution, send as guidance instead of regular message
    if (executionState === 'running' && onSendGuidance && trimmedMessage) {
      onSendGuidance(trimmedMessage);
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      return;
    }

    if ((!trimmedMessage && selectedFiles.length === 0) || isLoading || disabled) {
      return;
    }

    // Upload files first if any
    let fileIds: string[] = [];
    if (selectedFiles.length > 0) {
      fileIds = await uploadFiles();

      // Check if all uploads succeeded
      if (fileIds.length !== selectedFiles.length) {
        // Some uploads failed, don't send message
        return;
      }
    }

    // Send message with file IDs
    onSendMessage(trimmedMessage || 'Please analyze the attached files.', fileIds.length > 0 ? fileIds : undefined);

    // Reset state
    setMessage('');
    setSelectedFiles([]);
    setUploadedFileIds([]);
    setUploadProgress({});
    setUploadErrors({});

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const uploadFiles = async (): Promise<string[]> => {
    const fileIds: string[] = [];

    // Import upload function
    const { chatApi } = await import('@/services/chat');

    // Get session ID from store, not localStorage
    const sessionId = currentSession?.id || storeSessionId || '';

    if (!sessionId) {
      console.error('No active session - cannot upload file');
      setUploadErrors(prev => ({
        ...prev,
        '_session': 'No active session. Please start a chat first.'
      }));
      return [];
    }

    for (const file of selectedFiles) {
      try {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

        const uploadResponse = await chatApi.uploadFile(
          sessionId,
          file,
          (progress) => {
            setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
          }
        );

        fileIds.push(uploadResponse.file_id);
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
      } catch (error) {
        console.error('File upload failed:', error);
        setUploadErrors(prev => ({
          ...prev,
          [file.name]: 'Upload failed'
        }));
      }
    }

    return fileIds;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // ESC key - Interrupt execution (like Claude Code)
    if (e.key === 'Escape') {
      if (canInterrupt && onInterrupt) {
        e.preventDefault();
        console.log('🛑 ESC pressed - interrupting execution');
        onInterrupt();
        return;
      }
    }

    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // If executing and has guidance handler, send as guidance
      if (executionState === 'running' && onSendGuidance && message.trim()) {
        onSendGuidance(message.trim());
        setMessage('');
        return;
      }
      handleSubmit();
    }
  };

  // FEATURE: Manual reload of prompts and skills from database
  // PURPOSE: Force backend to reload latest prompts/skills without waiting for cache expiration
  // ENDPOINT: POST /api/prompts/reload (backend: accountantAI/src/api/prompts/handlers.rs)
  // CALLED BY: Reload button (P&S icon + refresh icon) in chat input
  // NOTE: This bypasses the 5-minute cache and loads fresh data immediately
  const handleReloadPromptsSkills = async () => {
    try {
      setIsReloading(true);

      // Import chatApi to make the API call
      const { api } = await import('@/services/api');

      // Call the reload endpoint
      const response = await api.post('/api/prompts/reload');

      const data = response.data;

      // Show success message with details
      console.log('✅ Prompts and skills reloaded:', {
        prompts: data.prompts_count,
        skills: data.skills_count,
        message: data.message,
        reloadedAt: data.reloaded_at
      });

      // Optional: Show toast notification (if toast library is available)
      // toast.success(`Reloaded ${data.prompts_count} prompts and ${data.skills_count} skills`);

    } catch (error) {
      console.error('❌ Failed to reload prompts/skills:', error);
      // Optional: Show error toast
      // toast.error('Failed to reload prompts and skills');
    } finally {
      setIsReloading(false);
    }
  };

  const remainingChars = CHARACTER_LIMIT - message.length;
  const isOverLimit = remainingChars < 0;
  const isUploading = Object.values(uploadProgress).some(p => p < 100 && p > 0);

  return (
    <form onSubmit={handleSubmit} className="relative">
      {/* File attachments preview - shown BEFORE upload */}
      {selectedFiles.length > 0 && (
        <div className="mb-2 space-y-2">
          {selectedFiles.map((file, index) => (
            <FileAttachment
              key={`${file.name}-${index}`}
              file={file}
              uploadProgress={uploadProgress[file.name]}
              uploadError={uploadErrors[file.name]}
              onRemove={() => removeFile(index)}
            />
          ))}
        </div>
      )}

      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            canInterrupt
              ? "Type guidance for the agent... (ESC to interrupt)"
              : placeholder
          }
          disabled={disabled || isUploading || (isLoading && !canInterrupt)}
          className={cn(
            "min-h-[44px] max-h-[120px] pr-20 resize-none",
            "transition-all duration-200",
            isOverLimit && "border-destructive focus-visible:ring-destructive",
            canInterrupt && "border-amber-500 focus-visible:ring-amber-500"
          )}
          rows={1}
        />

        {/* FEATURE: Reload prompts & skills button */}
        {/* PURPOSE: Manual reload of prompts/skills from database */}
        {/* ICON: P&S (book/database) + Refresh (rotate) icon combined */}
        {/* TRACEABILITY: Calls POST /api/prompts/reload endpoint */}
        {/* LOCATION: Before send button, after file attach button */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleReloadPromptsSkills}
          disabled={disabled || isLoading || isUploading || isReloading}
          className="absolute bottom-1.5 right-[4.5rem] h-7 w-7 p-0 group"
          title="Reload prompts and skills from database"
        >
          <div className="relative">
            {isReloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {/* Layered icons: Book (P&S) as base, Refresh overlay */}
                <span className="text-xs font-semibold opacity-70 group-hover:opacity-100 transition-opacity">
                  P&S
                </span>
                <RotateCw className="h-3 w-3 absolute -top-1 -right-1 opacity-70 group-hover:opacity-100 transition-opacity" />
              </>
            )}
          </div>
        </Button>

        {/* Attach file button */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isLoading || isUploading}
          className="absolute bottom-1.5 right-10 h-7 w-7 p-0"
        >
          <Paperclip className="h-4 w-4" />
        </Button>

        {/* Send button - enabled during execution for guidance */}
        <Button
          type="submit"
          size="sm"
          disabled={
            (!message.trim() && selectedFiles.length === 0 && !canInterrupt) ||
            (isLoading && !canInterrupt) ||
            disabled ||
            isOverLimit ||
            isUploading
          }
          className={cn(
            "absolute bottom-1.5 right-1.5 h-7 w-7 p-0",
            canInterrupt && message.trim() && "bg-amber-500 hover:bg-amber-600"
          )}
          title={canInterrupt ? "Send guidance to agent" : "Send message"}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isLoading && !canInterrupt ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        className="hidden"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.tiff,.csv,.xlsx,.xls"
      />

      {/* Character limit indicator and keyboard hints */}
      {(remainingChars < 100 || isOverLimit || canInterrupt || executionState === 'running') && (
        <div className="flex items-center justify-between mt-1 px-1">
          <span className="text-xs text-muted-foreground">
            {canInterrupt ? (
              <>
                <span className="text-amber-500 font-medium">Press ESC to interrupt</span>
                {' • Enter to send guidance'}
              </>
            ) : executionState === 'running' ? (
              'Processing... Enter to send guidance'
            ) : (
              'Press Enter to send, Shift+Enter for new line'
            )}
          </span>
          {(remainingChars < 100 || isOverLimit) && (
            <span
              className={cn(
                "text-xs",
                remainingChars < 100 ? "text-warning" : "text-muted-foreground",
                isOverLimit && "text-destructive font-medium"
              )}
            >
              {remainingChars} characters remaining
            </span>
          )}
        </div>
      )}
    </form>
  );
};
